import { useFrame, useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { Group, Raycaster, Vector2 } from "three";

const LEFT_MOUSE_BUTTON = 0;
const INTERSECTION_Y_OFFSET = 1.5;

// Handles mouse clicks in the 3D office
export const useCustomRayCaster = () => {
  const { camera, scene, pointer } = useThree();
  const raycaster = new Raycaster();

  useFrame(() => {
    const x = pointer.x * 2 - 1;
    const y = -(pointer.y * 2) + 1;

    raycaster.setFromCamera(new Vector2(x, y), camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    scene.traverse((object) => {
      if (object instanceof Group && (object as Group).isGroup) {
        const isIntersected = intersects.some(
          (intersect) =>
            intersect.object.parent === object ||
            intersect.object.parent?.parent === object
        );

        if (isIntersected) {
          object.dispatchEvent({ type: "pointerover" });
        } else {
          object.dispatchEvent({ type: "pointerout" });
        }
      }
    });
  });

  useEffect(() => {
    // This helper finds what the user clicked on and dispatches a custom event with the intersection data
    const findIntersectionAndDispatchEvent = ({
      userDataKey,
      eventType,
      intersects,
    }: {
      userDataKey: string;
      eventType: string;
      intersects: ReturnType<typeof raycaster.intersectObjects>;
    }) => {
      const intersection = intersects.find(
        (intersect) =>
          intersect.object.parent?.userData?.[userDataKey] ||
          intersect.object.parent?.parent?.userData?.[userDataKey]
      );

      if (intersection) {
        window.dispatchEvent(
          new CustomEvent(eventType, {
            detail: {
              position: [
                intersection.point.x,
                intersection.point.y + INTERSECTION_Y_OFFSET,
                intersection.point.z,
              ],
              [userDataKey]:
                intersection.object.parent?.userData?.[userDataKey] ||
                intersection.object.parent?.parent?.userData?.[userDataKey],
            },
          })
        );
      }
    };

    const handleContextMenu = (event: PointerEvent) => {
      if (!event.target) {
        return;
      }

      const targetElement = event.target as Element;

      // If clicking inside context menu, let browser handle it
      if (targetElement.closest("[data-context-menu]")) {
        return;
      }

      const canvas = targetElement.closest("canvas");
      if (!canvas) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new Vector2(x, y), camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      findIntersectionAndDispatchEvent({
        eventType: "room-context-menu",
        intersects,
        userDataKey: "roomId",
      });
      findIntersectionAndDispatchEvent({
        eventType: "table-context-menu",
        intersects,
        userDataKey: "tableId",
      });
      findIntersectionAndDispatchEvent({
        eventType: "staircase-context-menu",
        intersects,
        userDataKey: "staircaseId",
      });
    };

    // Also add handler for left-click on staircase
    const handleLeftClick = (event: PointerEvent) => {
      if (!event.target) {
        return;
      }

      const targetElement = event.target as Element;

      // Ignore right clicks and clicks inside context menu
      if (
        event.button !== LEFT_MOUSE_BUTTON ||
        targetElement.closest("[data-context-menu]")
      ) {
        return;
      }

      const canvas = targetElement.closest("canvas");
      if (!canvas) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new Vector2(x, y), camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      findIntersectionAndDispatchEvent({
        eventType: "staircase-context-menu",
        intersects,
        userDataKey: "staircaseId",
      });
    };

    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("click", handleLeftClick);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("click", handleLeftClick);
    };
  }, [camera, raycaster, scene]);
};
